/**
 * Microsoft-style avatar colors
 * Generates consistent colors based on a string identifier (name, email, or initials)
 */

// Microsoft 365 inspired color palette
const AVATAR_COLORS = [
  { bg: 'bg-[#0078D4]', text: 'text-white' }, // Microsoft Blue
  { bg: 'bg-[#107C10]', text: 'text-white' }, // Green
  { bg: 'bg-[#5C2D91]', text: 'text-white' }, // Purple
  { bg: 'bg-[#D83B01]', text: 'text-white' }, // Orange
  { bg: 'bg-[#008272]', text: 'text-white' }, // Teal
  { bg: 'bg-[#B4009E]', text: 'text-white' }, // Magenta
  { bg: 'bg-[#C239B3]', text: 'text-white' }, // Pink
  { bg: 'bg-[#00B7C3]', text: 'text-white' }, // Cyan
  { bg: 'bg-[#FFB900]', text: 'text-black' }, // Yellow
  { bg: 'bg-[#E74856]', text: 'text-white' }, // Red
  { bg: 'bg-[#0099BC]', text: 'text-white' }, // Light Blue
  { bg: 'bg-[#7A7574]', text: 'text-white' }, // Gray
  { bg: 'bg-[#567C73]', text: 'text-white' }, // Sage
  { bg: 'bg-[#69797E]', text: 'text-white' }, // Steel
  { bg: 'bg-[#647C64]', text: 'text-white' }, // Forest
  { bg: 'bg-[#038387]', text: 'text-white' }, // Dark Teal
] as const

/**
 * Simple hash function to convert a string to a number
 */
function hashString(str: string): number {
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i)
    hash = ((hash << 5) - hash) + char
    hash = hash & hash // Convert to 32bit integer
  }
  return Math.abs(hash)
}

/**
 * Get avatar color classes based on identifier string
 * Returns consistent color for the same identifier
 */
export function getAvatarColor(identifier: string): { bg: string; text: string } {
  const hash = hashString(identifier.toLowerCase())
  const index = hash % AVATAR_COLORS.length
  const color = AVATAR_COLORS[index]
  return color ?? AVATAR_COLORS[0]
}

/**
 * Get initials from name or email
 */
export function getInitials(firstName?: string | null, lastName?: string | null, email?: string | null): string {
  if (firstName && lastName) {
    return `${firstName[0]}${lastName[0]}`.toUpperCase()
  }
  if (firstName) {
    return firstName.substring(0, 2).toUpperCase()
  }
  if (email) {
    const localPart = email.split('@')[0] || ''
    if (localPart.includes('.')) {
      const parts = localPart.split('.')
      const first = parts[0] || ''
      const last = parts[1] || ''
      if (first && last) {
        return `${first[0]}${last[0]}`.toUpperCase()
      }
    }
    return localPart.substring(0, 2).toUpperCase() || 'U'
  }
  return 'U'
}

/**
 * Get avatar props (color and initials) for a user
 */
export function getAvatarProps(user: {
  id?: string
  firstName?: string | null
  lastName?: string | null
  email?: string | null
  fullName?: string | null
}): { initials: string; colorClasses: string } {
  const initials = getInitials(user.firstName, user.lastName, user.email)
  const identifier = user.id || user.email || user.fullName || initials
  const color = getAvatarColor(identifier)
  return {
    initials,
    colorClasses: `${color.bg} ${color.text}`,
  }
}
